(() => {
  const GRID_SELECTOR = '[data-ukfc-preorder-priority]';
  const CARD_SELECTOR = ':scope > [data-ukfc-priority]';

  function priorityOf(card) {
    return card.dataset.ukfcPriority || 'normal';
  }

  class UkfcSearchPriority {
    constructor(grid) {
      if (grid.dataset.ukfcPriorityReady === 'true') return;
      grid.dataset.ukfcPriorityReady = 'true';
      this.grid = grid;
      this.container = grid.closest('#ProductGridContainer');
      this.sectionId = grid.dataset.ukfcPrioritySection;
      this.pageSize = Math.max(1, grid.querySelectorAll(CARD_SELECTOR).length);
      this.sourcePage = Number(new URL(window.location.href).searchParams.get('page') || '1');
      this.hasNext = this.readHasNext(this.container);
      this.normal = [];
      this.preorder = [];
      this.soldout = [];
      this.staticNodes = Array.from(grid.children).filter((node) => !node.matches('[data-ukfc-priority]'));
      this.controls = this.createControls();
      this.capture(Array.from(grid.querySelectorAll(CARD_SELECTOR)));
      grid.replaceChildren(...this.staticNodes);
      this.nativePagination = Array.from(this.container?.querySelectorAll('.pagination, [data-show-more]') || []);
      this.removeNativePagination();
      this.grid.insertAdjacentElement('afterend', this.controls);

      const firstBatch = this.normal.splice(0, this.pageSize);
      this.appendCards(firstBatch);
      const remainingSlots = this.pageSize - firstBatch.length;
      if (remainingSlots > 0 && this.hasNext) this.loadNext(remainingSlots);
      else this.updateControls();
    }

    readHasNext(scope) {
      return scope?.querySelector('[data-ukfc-priority-meta]')?.dataset.ukfcHasNext === 'true';
    }

    capture(cards) {
      cards.forEach((card) => {
        if (priorityOf(card) === 'preorder') this.preorder.push(card);
        else if (priorityOf(card) === 'soldout') this.soldout.push(card);
        else this.normal.push(card);
      });
    }

    createControls() {
      const controls = document.createElement('div');
      controls.className = 'ukfc-preorder-priority';
      controls.setAttribute('aria-live', 'polite');
      controls.innerHTML = '<button type="button" class="btn btn--secondary ukfc-preorder-priority__button" hidden>Show more products</button>';
      controls.querySelector('button').addEventListener('click', () => this.loadNext());
      return controls;
    }

    updateControls() {
      const button = this.controls.querySelector('.ukfc-preorder-priority__button');
      const hasMoreNormalProducts = this.normal.length > 0 || this.hasNext;
      button.hidden = !hasMoreNormalProducts;
      button.style.display = hasMoreNormalProducts ? '' : 'none';
      if (!hasMoreNormalProducts) this.appendDeferred();
    }

    removeNativePagination() {
      this.container?.querySelectorAll('.pagination, [data-show-more]').forEach((element) => element.remove());
    }

    async fetchNextPage() {
      this.sourcePage += 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(this.sourcePage));
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Could not load more results.');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = documentFragment.querySelector(GRID_SELECTOR);
      if (!nextGrid) throw new Error('Could not read the next results page.');
      this.capture(Array.from(nextGrid.querySelectorAll(CARD_SELECTOR)));
      this.hasNext = this.readHasNext(documentFragment);
    }

    appendCards(cards) {
      cards.forEach((card) => this.grid.appendChild(card));
    }

    appendGroup(cards, heading, message) {
      if (!cards.length) return;
      const divider = document.createElement('div');
      divider.className = 'ukfc-preorder-priority__divider';
      divider.innerHTML = '<h2 class="h4 m-0"></h2><p class="m-0 text-subtext"></p>';
      divider.querySelector('h2').textContent = heading;
      divider.querySelector('p').textContent = message;
      this.grid.appendChild(divider);
      this.appendCards(cards.splice(0));
    }

    appendDeferred() {
      if (this.deferredAppended) return;
      this.deferredAppended = true;
      this.appendGroup(this.preorder, 'Available to order', 'These products are not normally dispatched from stock.');
      this.appendGroup(this.soldout, 'Currently unavailable', 'These products are shown after items that can be ordered or dispatched now.');
    }

    async loadNext(limit = this.pageSize) {
      if (this.loading) return;
      this.loading = true;
      const button = this.controls.querySelector('button');
      button.disabled = true;
      button.hidden = true;
      button.style.display = 'none';
      this.grid.setAttribute('aria-busy', 'true');

      try {
        if (!this.normal.length && this.hasNext) await this.fetchNextPage();
        this.appendCards(this.normal.splice(0, limit));
        this.updateControls();
      } catch (error) {
        this.appendCards(this.normal.splice(0));
        this.appendDeferred();
        button.hidden = true;
        button.style.display = 'none';
        this.nativePagination.forEach((element) => this.container?.appendChild(element));
        console.error(error);
      } finally {
        this.grid.removeAttribute('aria-busy');
        button.disabled = false;
        this.loading = false;
      }
    }
  }

  function initialise() {
    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => new UkfcSearchPriority(grid));
  }

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initialise();
    });
  });

  document.addEventListener('DOMContentLoaded', initialise);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  initialise();
})();